import * as React from "react";
import Select from 'react-select';
import {none, option, Option} from "ts-option";
import {PlaceholderProps} from "../../../common/props/PlaceholderProps";
import {SelectComponentDefaultValue} from "../../../ui/select/SelectComponent";
import {ActionMeta, InputActionMeta} from "react-select/lib/types";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import {api} from "../../../../api/api";
import MaterialSearch from "../../../../model/material/MaterialSearch";
import CurrencyHelper from "../../../../helper/CurrencyHelper";
import classNames = require("classnames");

interface ComponentProps {
	/**
	 * Callback on select material.
	 * @param {MaterialSearch} material.
	 */
	onSelect: (material: MaterialSearch) => void;
	/**
	 * Initial search query text.
	 */
	initialSearchQuery?: Option<string>
	/**
	 * Merchant id
	 */
	bomMerchantId: string;
}

interface State {
	/**
	 * Text typed by user.
	 */
	queryText: string;
	/**
	 * Materials available for user selection.
	 */
	materials: MaterialSearch[];
	/**
	 * Indicates if data is loading.
	 */
	loading: boolean;
	/**
	 * Last request query.
	 */
	lastRequestedQuery: Option<string>;
}

const minCharsToSearch = 2;

type Props = ComponentProps & PlaceholderProps;

export default class MaterialSelectionComponent extends React.PureComponent<Props, State> {
	public static defaultProps: Partial<Props> = {
		initialSearchQuery: none
	};

	constructor(props: Props) {
		super(props);
		this.onInputChange = this.onInputChange.bind(this);
		this.onSelected = this.onSelected.bind(this);
		this.renderNoOptionsMessage = this.renderNoOptionsMessage.bind(this);
		const initialQueryText = this.props.initialSearchQuery ? this.props.initialSearchQuery.getOrElse("") : "";
		this.state = {
			queryText: initialQueryText,
			materials: [],
			loading: false,
			lastRequestedQuery: none
		};
	}

	render() {
		const value = this.state.queryText.length ?
			new SelectComponentDefaultValue(this.state.queryText, this.state.queryText) : null;
		return (
			<Select
				classNamePrefix={"c-select-component"}
				inputValue={this.state.queryText}
				value={value}
				styles={{menuList: (base) => ({...base, maxHeight: "186px"})}}
				placeholder={this.props.placeholder}
				filterOption={(element: any, filter: string) => true}
				components={{Option: this.renderOptionComponent, Menu: this.renderMenuComponent, NoOptionsMessage: this.renderNoOptionsMessage}}
				options={this.state.materials}
				isLoading={this.state.loading}
				onInputChange={this.onInputChange}
				onChange={this.onSelected}
				closeMenuOnSelect={true}
				openMenuOnFocus={true}/>
		)
	}

	/**
	 * Render option component (select dropdown list element)
	 * @param props properties for list element.
	 */
	renderOptionComponent(props: any): JSX.Element {
		const {innerProps, innerRef, isFocused, data, className} = props;
		const classFromProps = classNames(
			{
				"c-selection-bom-modal_select-option-component": true,
				"has-focus": isFocused
			},
			className
		);

		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className="c-selection-bom-modal_select-option-component">
					<div className="c-selection-bom-modal_select-option-component_labels">
						<div>
							<span className="c-selection-bom-modal_select-option-component_labels_column">
								<b className="c-selection-bom-modal_select-option-component_labels_blue-color">
									{data.description}</b>
							</span>
						</div>
						<div className="c-selection-bom-modal_select-option-component_labels_blue-color fit-content">
							<span><b>{data.price.map(CurrencyHelper.toCurrency).orNull}</b></span>
						</div>
					</div>
				</div>
			</div>
		)
	}

	/**
	 * Render menu component (wrapper for the dropdown menu in the select)
	 * @param props properties for list element.
	 */
	renderMenuComponent(props: any): JSX.Element {
		const {innerProps, innerRef, className} = props;
		const classFromProps = classNames(
			{
				"bom-selection": true
			},
			className
		);
		return (
			<div ref={innerRef} className={classFromProps} {...innerProps}>
				<div className={'selection-header'}>
					<div className={'header-title'}>
						<span>Description</span>
						<span>Buy price</span>
					</div>
					{props.children}
				</div>
			</div>
		);
	}

	/**
	 * Render no options component (Message to be displayed in the menu if there are no options passed in)
	 */
	renderNoOptionsMessage(): JSX.Element {
		if (this.state.loading)
			return <div>Loading...</div>;
		else if (this.state.queryText.length >= minCharsToSearch)
			return <div className="no-option">No results for '<span className="query">{this.state.queryText}</span>'</div>;
		else
			return <div className="no-option">Type to search</div>;
	}

	/**
	 * On changed value in select, fetch data from store or fetch from server.
	 * @param searchText text in the input.
	 * @param {InputActionMeta} inputActionMeta
	 */
	onInputChange(searchText: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: searchText});
			if (searchText.length >= minCharsToSearch) {
				this.findBySearchText(searchText);
			}
			else {
				this.setState({materials: [], loading: false});
			}
		}
	}

	/**
	 * Callback on selected item.
	 * @param {MaterialSearch} selected item.
	 * @param {ActionMeta} actionMeta performed action data.
	 */
	onSelected(selected: MaterialSearch, actionMeta: ActionMeta) {
		if (actionMeta.action === 'clear') {
			this.setState({queryText: "", materials: []});
		}
		// compare with array is fix for https://github.com/JedWatson/react-select/issues/2682
		else if (!(selected instanceof Array)) {
			this.props.onSelect(selected);
		}
	}

	/**
	 * Reset search input text.
	 */
	public resetSearchInput() {
		this.setState({queryText: ""});
	}

	/**
	 * Fetch materials by search text.
	 * @param {string} searchText text to search by.
	 */
	private findBySearchText(searchText: string) {
		this.setState({loading: true, lastRequestedQuery: option(searchText)}, () => {
			api.instance.merchantApi.findMaterialsByText(this.props.bomMerchantId, searchText).then(result => {
				this.setState({materials: result, loading: false});
			}).catch((error) => {
				this.setState({materials: [], loading: false});
				notifyBugsnagAndShowToast(error, "Error on searching materials");
			});
		});
	}
}
